import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { X, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import Wordmark from '../components/Wordmark';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/requests';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // navigate('/') instead of navigate(-1): when /login is opened directly
    // (fresh tab, external link) there is no history to go back to.
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)', fontFamily: 'var(--font-body)' }}>
      <div className="w-full max-w-md rounded-[var(--s8-radius-lg)] overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--b1-solid)' }}>
        {/* Header */}
        <div className="p-8 pb-6 relative text-center" style={{ background: 'var(--bg-card-inner)' }}>
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 rounded-[var(--s8-radius-md)] transition-colors"
            style={{ color: 'var(--t3)' }}
            aria-label="Close"
            data-testid="login-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
          <Wordmark className="h-[40px] mx-auto" />
          <p className="mt-2 text-sm" style={{ color: 'var(--t2)' }}>
            Enter your credentials to access your account
          </p>
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px solid var(--b1-solid)' }} />

        {/* Form */}
        <div className="p-6 pt-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider" style={{ fontFamily: 'var(--font-ui)', color: 'var(--t3)' }}>Email</Label>
              <Input
                id="email"
                name="login-email"
                type="email"
                value={email}
                defaultValue=""
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="username"
                className="h-12 rounded-[var(--s8-radius-md)]"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider" style={{ fontFamily: 'var(--font-ui)', color: 'var(--t3)' }}>Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 10 characters"
                  required
                  autoComplete="current-password"
                  className="h-12 rounded-[var(--s8-radius-md)] pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-0 top-0 h-12 w-12 flex items-center justify-center"
                  style={{ color: 'var(--t3)' }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="login-toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {/* No self-serve reset flow yet — fall back to support mailto. */}
              <div className="text-right">
                <a
                  href="mailto:support@stage8.io?subject=Password%20reset%20request"
                  className="text-xs font-medium"
                  style={{ color: 'var(--amber)' }}
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </a>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full mt-6 h-12 text-sm font-semibold uppercase tracking-wider rounded-[var(--s8-radius-pill)]"
              disabled={loading}
              style={{ fontFamily: 'var(--font-ui)', background: 'var(--amber)', color: 'var(--bg)' }}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span style={{ color: 'var(--t3)' }}>Don't have an account? </span>
            <Link to="/signup" className="font-medium" style={{ color: 'var(--amber)' }} data-testid="signup-link">
              Sign up
            </Link>
          </div>

          <div className="flex items-center justify-center gap-2 mt-5 pt-4 text-xs" style={{ color: 'var(--t3)', borderTop: '1px solid var(--b1-solid)' }}>
            <Shield className="h-3.5 w-3.5" />
            <span>Secure login protected by STAGE8</span>
          </div>
        </div>
      </div>
    </div>
  );
}
